import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  SignalIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import StatusBadge from '../common/StatusBadge';

const PlatformHealthDashboard = ({ platformHealth, loading }) => {
  const [refreshing, setRefreshing] = useState(false);

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin-600"></div>
          <p className="ml-4 text-sm text-gray-500">Loading platform health data...</p>
        </div>
      </div>
    );
  }

  if (!platformHealth) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="text-center py-20">
          <GlobeAltIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Health Data</h3>
          <p className="text-gray-500">Platform health data is not available</p>
        </div>
      </div>
    );
  }

  const { platforms, overall_health } = platformHealth;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'outage':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'text-green-700 bg-green-100 border-green-200';
      case 'degraded':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      case 'outage':
        return 'text-red-700 bg-red-100 border-red-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: '📘',
      instagram: '📷',
      twitter: '🐦',
      linkedin: '💼',
      youtube: '📺',
      tiktok: '🎵',
      threads: '🧵'
    };
    return icons[platform] || '🌐';
  };

  const formatResponseTime = (ms) => {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatUptime = (percentage) => {
    return `${percentage.toFixed(2)}%`;
  };

  const getRateLimitStatus = (rateLimit) => {
    const usagePercentage = ((rateLimit.calls_per_hour - rateLimit.remaining) / rateLimit.calls_per_hour) * 100;
    
    if (usagePercentage >= 90) {
      return { status: 'critical', color: 'text-red-600' };
    } else if (usagePercentage >= 75) {
      return { status: 'warning', color: 'text-yellow-600' };
    }
    return { status: 'good', color: 'text-green-600' };
  };

  return (
    <div className="space-y-6">
      {/* Overall Health Summary */}
      <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Platform Health Overview</h2>
          <button
            onClick={() => setRefreshing(!refreshing)}
            className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <GlobeAltIcon className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{overall_health.total_platforms}</p>
            <p className="text-sm text-gray-600">Total Platforms</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{overall_health.operational_platforms}</p>
            <p className="text-sm text-gray-600">Operational</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-lg flex items-center justify-center mb-3">
              <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{overall_health.degraded_platforms}</p>
            <p className="text-sm text-gray-600">Degraded</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-lg flex items-center justify-center mb-3">
              <XCircleIcon className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{overall_health.outage_platforms}</p>
            <p className="text-sm text-gray-600">Outages</p>
          </div>
        </div>
      </div>

      {/* Platform Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.values(platforms).map((platform) => {
          const rateLimitStatus = getRateLimitStatus(platform.rate_limits);
          
          return (
            <div key={platform.platform} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              {/* Platform Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{getPlatformIcon(platform.platform)}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">
                      {platform.platform}
                    </h3>
                    <p className="text-sm text-gray-500">
                      Last checked: {new Date(platform.last_check).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                
                <div className={`flex items-center px-3 py-1 rounded-full border ${getStatusColor(platform.status)}`}>
                  {getStatusIcon(platform.status)}
                  <span className="ml-2 text-sm font-medium capitalize">{platform.status}</span>
                </div>
              </div>

              {/* Platform Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <SignalIcon className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-sm text-gray-600">Response Time</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {formatResponseTime(platform.avg_response_time_ms)}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-gray-600">Uptime</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {formatUptime(platform.uptime_percentage)}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <GlobeAltIcon className="w-4 h-4 text-blue-500 mr-2" />
                    <span className="text-sm text-gray-600">Connections</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {platform.total_connections}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {platform.healthy_connections} healthy, {platform.error_connections} errors
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <ClockIcon className={`w-4 h-4 mr-2 ${rateLimitStatus.color}`} />
                    <span className="text-sm text-gray-600">Rate Limits</span>
                  </div>
                  <p className={`text-lg font-semibold mt-1 ${rateLimitStatus.color}`}>
                    {platform.rate_limits.remaining}/{platform.rate_limits.calls_per_hour}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Resets at {new Date(platform.rate_limits.reset_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>

              {/* Rate Limited Connections */}
              {platform.rate_limited_connections > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 mr-2" />
                    <span className="text-sm text-yellow-800">
                      {platform.rate_limited_connections} connections are rate limited
                    </span>
                  </div>
                </div>
              )}

              {/* Recent Incidents */}
              {platform.recent_incidents && platform.recent_incidents.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Recent Incidents</h4>
                  <div className="space-y-2">
                    {platform.recent_incidents.map((incident) => (
                      <div key={incident.id} className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-red-900">{incident.title}</p>
                            <p className="text-xs text-red-700 mt-1">
                              Started: {new Date(incident.started_at).toLocaleString()}
                            </p>
                          </div>
                          <StatusBadge status={incident.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Health Summary Footer */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Platform health data is updated every 5 minutes. Last update: {platformHealth.generated_at ? new Date(platformHealth.generated_at).toLocaleString() : 'Unknown'}
        </p>
      </div>
    </div>
  );
};

export default PlatformHealthDashboard;